/**
 * The directory, on this device (yourplace yp-5, docs/YOUR_PLACE_SPEC.md §3.1,
 * §4.3): which server this browser talks to.
 *
 * Almost every business keeps its books on Karr Cloud, the address this app was
 * built with, and for them nothing here asks the network: the client is built
 * from the build's own address, as before. A business on a server of its own
 * (an own place) is found once, at sign-in: `get_place_for_email` on Karr Cloud
 * answers the places an email signs in at (Karr Cloud for any email it does not
 * know). The answer is kept on the device, so the next sign-in asks nothing, and
 * the chosen place is stored where `src/db/supabase.ts` reads it before the app
 * draws:
 *
 *   localStorage['karr.place'] = { id, kind, label, url, publishable_key, release, behind, build_url }
 *
 * the same key the older-build redirect reads (src/shared/release/olderBuild.ts).
 *
 * Only the directory writes a place. A link cannot: an address in a link that
 * pointed this app at another server would sign people in to whoever made the
 * link. And a place the directory marks as a copy, sealed or registering is
 * never stored (law 1: one writer).
 */
import { PLACE_KEY } from '@/shared/release/olderBuild';

export interface DirectoryPlace {
    id: string;
    kind: 'karr_cloud' | 'own_cloud' | 'own_cloud_managed' | 'office_box';
    label: string;
    url: string | null;
    publishable_key: string | null;
    release: number | null;
    status: 'active' | 'moving';
    behind: boolean;
    build_url: string | null;
}

/** The directory's answers on this device, by lowered email. */
const ANSWERS_KEY = 'karr.directory';
/** An email to send the sign-in link to once the app has switched place. */
export const SEND_AFTER_SWITCH_KEY = 'karr.place.send';
/** An answer older than this is asked again at the next sign-in. */
const ANSWER_DAYS = 30;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const local = (): Store | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const HTTPS = /^https:\/\/[a-z0-9.-]+(:\d+)?$/;

function isPlace(p: unknown): p is DirectoryPlace {
    const x = p as DirectoryPlace;
    return !!x && typeof x.id === 'string' && typeof x.label === 'string'
        && ['karr_cloud', 'own_cloud', 'own_cloud_managed', 'office_box'].includes(x.kind)
        && (x.kind === 'karr_cloud' || (typeof x.url === 'string' && HTTPS.test(x.url) && typeof x.publishable_key === 'string'))
        && (x.status === 'active' || x.status === 'moving');
}

/** The own place this device signs in at, or null for Karr Cloud. */
export function storedPlace(store: Store | null = local()): DirectoryPlace | null {
    try {
        const p = JSON.parse(store?.getItem(PLACE_KEY) ?? 'null');
        return isPlace(p) && p.kind !== 'karr_cloud' ? p : null;
    } catch {
        return null;
    }
}

/** The server the app's client is built for: the stored own place, else the build's own. */
export function clientTarget(buildUrl: string, buildKey: string, store: Store | null = local()): { url: string; key: string; place: DirectoryPlace | null } {
    const p = storedPlace(store);
    if (p?.url && p.publishable_key) return { url: p.url, key: p.publishable_key, place: p };
    return { url: buildUrl, key: buildKey, place: null };
}

/** The server this app talks to (for a bare fetch to a function): the own place's, or the build's own. */
export function serverUrl(): string {
    return clientTarget(String(import.meta.env.VITE_SUPABASE_URL ?? ''), String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '')).url;
}

/** The directory's last answer for this email on this device, or null when it must ask. */
export function rememberedPlaces(email: string, store: Store | null = local(), now = Date.now()): DirectoryPlace[] | null {
    try {
        const all = JSON.parse(store?.getItem(ANSWERS_KEY) ?? '{}') as Record<string, { at: number; places: unknown[] }>;
        const hit = all[email.trim().toLowerCase()];
        if (!hit || now - hit.at > ANSWER_DAYS * 86_400_000) return null;
        const places = hit.places.filter(isPlace);
        return places.length ? places : null;
    } catch {
        return null;
    }
}

function rememberPlaces(email: string, places: DirectoryPlace[], store: Store | null, now: number) {
    try {
        const all = JSON.parse(store?.getItem(ANSWERS_KEY) ?? '{}') as Record<string, unknown>;
        all[email.trim().toLowerCase()] = { at: now, places };
        store?.setItem(ANSWERS_KEY, JSON.stringify(all));
    } catch { /* storage blocked: the next sign-in asks again */ }
}

/** Karr Cloud, as the directory says it when nothing else is known. */
export const KARR_CLOUD: DirectoryPlace = {
    id: 'karr_cloud', kind: 'karr_cloud', label: 'Karr Cloud · Mumbai, India', url: null, publishable_key: null,
    release: null, status: 'active', behind: false, build_url: null,
};

/**
 * The places this email signs in at. Asked of Karr Cloud once per device (the
 * answer is kept 30 days); when the directory cannot be asked, Karr Cloud, so a
 * sign-in never waits on it.
 */
export async function placesForEmail(
    email: string,
    ask: (email: string) => Promise<unknown>,
    store: Store | null = local(),
    now = Date.now(),
): Promise<DirectoryPlace[]> {
    const known = rememberedPlaces(email, store, now);
    if (known) return known;
    try {
        const answer = (await ask(email)) as { places?: unknown[] } | null;
        const places = (answer?.places ?? []).filter(isPlace);
        if (!places.length) return [KARR_CLOUD];
        rememberPlaces(email, places, store, now);
        return places;
    } catch {
        return [KARR_CLOUD];
    }
}

/** Asks Karr Cloud's directory (never a place's): one call, no session needed. */
export async function askDirectory(email: string): Promise<unknown> {
    const base = String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '');
    const key = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '');
    const res = await fetch(`${base}/rest/v1/rpc/get_place_for_email`, {
        method: 'POST',
        headers: { apikey: key, 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_email: email.trim() }),
    });
    if (!res.ok) throw new Error(`directory ${res.status}`);
    return res.json();
}

/**
 * Makes `p` the place this device signs in at. True when the app must start
 * again to talk to it (the client is built once, before the app draws).
 */
export function choosePlace(p: DirectoryPlace, store: Store | null = local()): boolean {
    const now = storedPlace(store);
    try {
        if (p.kind === 'karr_cloud') {
            store?.removeItem(PLACE_KEY);
            return now !== null;
        }
        store?.setItem(PLACE_KEY, JSON.stringify(p));
        return now?.url !== p.url || now?.publishable_key !== p.publishable_key;
    } catch {
        return false;
    }
}
